import {
  ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Injectable, Input, OnChanges, OnInit, Output,
  signal, SimpleChanges
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {DateAdapter, MAT_DATE_FORMATS, MatDateFormats} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {
  MatDatepicker, MatDatepickerActions, MatDatepickerApply, MatDatepickerCancel, MatDatepickerInput,
  MatDatepickerInputEvent, MatDatepickerToggle
} from '@angular/material/datepicker';
import {MatError, MatFormField, MatInput, MatSuffix} from '@angular/material/input';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import moment, {Moment} from 'moment';
import {datepickerLocale} from '../../../services/format-datepicker';

const ISO_DAY = 'YYYY-MM-DD';

export const AVAILABLE_FROM_DATE_FORMATS: MatDateFormats = {
  parse: {dateInput: ['DD.MM.YYYY', 'D.MM.YYYY', 'DD.M.YYYY', 'D.M.YYYY']},
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Injectable()
export class AvailableFromDateAdapter extends MomentDateAdapter {
  override parse(value: unknown, parseFormat: string | string[]): Moment | null {
    return super.parse(typeof value === 'string' ? value.trim() : value, parseFormat);
  }
}

let nextId = 0;

@Component({
  selector: 'app-available-from-filter',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatFormField,
    MatInput,
    MatError,
    MatSuffix,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepickerActions,
    MatDatepickerCancel,
    MatDatepickerApply,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
  ],
  providers: [
    {provide: DateAdapter, useClass: AvailableFromDateAdapter},
    {provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {strict: true}},
    {provide: MAT_DATE_FORMATS, useValue: AVAILABLE_FROM_DATE_FORMATS},
  ],
  templateUrl: './available-from-filter.component.html',
  styleUrl: './available-from-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailableFromFilterComponent implements OnInit, OnChanges {
  private readonly adapter = inject<DateAdapter<Moment>>(DateAdapter);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() value: string | null = null;
  @Output() valueChange = new EventEmitter<string | null>();

  readonly control = new FormControl<Moment | null>(null);
  readonly hasText = signal(false);
  readonly hintId = `available-from-hint-${nextId++}`;

  get hintDate(): string {
    if (!this.value) return '';
    const [year, month, day] = this.value.split('-');
    return `${day}.${month}.${year}`;
  }

  ngOnInit(): void {
    this.applyLocale(this.translate.getCurrentLang());
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({lang}) => this.applyLocale(lang));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['value']) return;
    if (!this.value) {
      if (this.isoOf(this.control.value) !== null) this.clear();
      return;
    }
    if (this.isoOf(this.control.value) === this.value) return;
    this.control.setValue(moment(this.value, ISO_DAY, true));
    this.hasText.set(true);
  }

  clear(): void {
    this.control.reset(null);
    this.hasText.set(false);
  }

  onInput(raw: string): void {
    const text = raw.trim();
    this.hasText.set(raw.length > 0);
    if (!text) {
      this.emit(null);
      return;
    }
    const parsed = this.adapter.parse(text, AVAILABLE_FROM_DATE_FORMATS.parse.dateInput);
    if (parsed && this.adapter.isValid(parsed)) this.emit(this.isoOf(parsed));
  }

  onDateChange(event: MatDatepickerInputEvent<Moment>): void {
    if (!event.value || !this.adapter.isValid(event.value)) return;
    this.hasText.set(true);
    this.emit(this.isoOf(event.value));
  }

  onClearClick(): void {
    this.clear();
    this.emit(null);
  }

  private emit(value: string | null): void {
    if (value === this.value) return;
    this.valueChange.emit(value);
  }

  private isoOf(date: Moment | null): string | null {
    return date && date.isValid() ? date.format(ISO_DAY) : null;
  }

  private applyLocale(lang: string | null | undefined): void {
    this.adapter.setLocale(datepickerLocale(lang));
  }
}
